import { useState, createContext, useEffect, useMemo, useContext } from 'react';
import type { PageMod } from '../../types/mod.types';
import type {
  PageModsContextValues,
  PageModsProviderProps,
} from '../../types/pageModsProvider.types';

const EMPTY_ARRAY_SINGLETON: PageMod[] = [];

/**
 * This context provides a wrapper that manages the page mods, which must be injected at different levels, for the application.
 * 
 * Page mods that are specified via the site configs should be passed in to the provider as props.
 * The expected structure of a single pageUxMod is:
  {    
    modRef: {
      family,
      id,
    },
    scope,
    enabled,
  }
 * ModWrappers should not pass mods with enabled === false into its ModBoss.
 * For this reason, the context exposes multiple "activeMod" properties for
 * each mod level to pass into a Mod Boss (e.g. activePageLevelPageUxMods)
 * 
 * The application's debug tools provide client's an interface for changing the set of page UX mods
 * in the application. For this reason, we expose the setter function for mods at each level,
 * as well as the ability to toggle a mod's enablement.
 * 
 * Note on performance: For production customers, the set of inbound props to this component will likely
 * not change over the course of their page visit. Changing page UX mods at runtime will really be a 
 * debug tools / developer operation. It could be more performant if we memoized the set of mods on each
 * level, and only update a given level's state if mods within it change. But for the time being, the 
 * existing behavior is fine, even if it causes state updates at every level when a page mod is changed.
 */

const PageModsContext = createContext<PageModsContextValues | null>(null);

const PageModsProvider = (props: PageModsProviderProps) => {
  const { children, pageUxMods = EMPTY_ARRAY_SINGLETON } = props;

  // Page Level Mods
  const [pageLevelPageUxMods, setPageLevelPageUxMods] = useState(
    pageUxMods.filter((mod) => mod.scope === 'page')
  );
  const activePageLevelPageUxMods = pageLevelPageUxMods.filter(
    (mod) => mod.enabled
  );

  // Order Level Mods
  const [orderLevelPageUxMods, setOrderLevelPageUxMods] = useState(
    pageUxMods.filter((mod) => mod.scope === 'order')
  );
  const activeOrderLevelPageUxMods = orderLevelPageUxMods.filter(
    (mod) => mod.enabled
  );

  // Item Level Mods
  const [itemLevelPageUxMods, setItemLevelPageUxMods] = useState(
    pageUxMods.filter((mod) => mod.scope === 'item')
  );
  const activeItemLevelPageUxMods = itemLevelPageUxMods.filter(
    (mod) => mod.enabled
  );

  // Item Group Level Mods
  const [itemGroupLevelPageUxMods, setItemGroupLevelPageUxMods] = useState(
    pageUxMods.filter((mod) => mod.scope === 'itemGroup')
  );
  const activeItemGroupLevelPageUxMods = itemGroupLevelPageUxMods.filter(
    (mod) => mod.enabled
  );

  useEffect(() => {
    setPageLevelPageUxMods(pageUxMods.filter((mod) => mod.scope === 'page'));
    setOrderLevelPageUxMods(pageUxMods.filter((mod) => mod.scope === 'order'));
    setItemLevelPageUxMods(pageUxMods.filter((mod) => mod.scope === 'item'));
    setItemGroupLevelPageUxMods(
      pageUxMods.filter((mod) => mod.scope === 'itemGroup')
    );
  }, [
    setPageLevelPageUxMods,
    setOrderLevelPageUxMods,
    setItemLevelPageUxMods,
    setItemGroupLevelPageUxMods,
    pageUxMods,
  ]);

  const pageModsContextValues = useMemo(
    () => ({
      pageLevelPageUxMods,
      setPageLevelPageUxMods,
      activePageLevelPageUxMods,
      orderLevelPageUxMods,
      setOrderLevelPageUxMods,
      activeOrderLevelPageUxMods,
      itemLevelPageUxMods,
      setItemLevelPageUxMods,
      activeItemLevelPageUxMods,
      itemGroupLevelPageUxMods,
      setItemGroupLevelPageUxMods,
      activeItemGroupLevelPageUxMods,
    }),
    [
      activeItemGroupLevelPageUxMods,
      activeItemLevelPageUxMods,
      activeOrderLevelPageUxMods,
      activePageLevelPageUxMods,
      itemGroupLevelPageUxMods,
      itemLevelPageUxMods,
      orderLevelPageUxMods,
      pageLevelPageUxMods,
    ]
  );

  return (
    <PageModsContext.Provider value={pageModsContextValues}>
      {children}
    </PageModsContext.Provider>
  );
};

const usePageModsContext = () => {
  const context = useContext(PageModsContext);
  if (!context) {
    throw new Error(
      'usePageModsContext must be used within an PageModsProvider'
    );
  }
  return context;
};

export { PageModsContext, PageModsProvider, usePageModsContext };
